import type { CSSProperties, ReactNode } from 'react'
import clsx from 'clsx'
import type { SaveSource } from '@app/shared'

import { Document, RedditFace, VideoBox, XMark } from './icons'

// ============================================================================
// The small repeated pieces of the Interests screen: the source tile that makes
// a wall of cards scannable, the filter pills in the band, and the two shapes a
// panel takes when it has nothing true to show yet.
// ============================================================================

/** Ground + glyph hue per source. The grounds are the mockup's own tints. */
const SOURCE_TILE: Record<SaveSource, string> = {
  reddit: 'bg-source-reddit-fill text-orange-2',
  youtube: 'bg-danger-fill text-danger-dot',
  article: 'bg-info-fill-2 text-source-web',
  // X has no tile in the mockup (it draws no x.com save). Neutral rather than
  // invented: a hue would be a claim about a brand this bundle does not carry.
  x: 'bg-fill text-muted',
}

/** The 5px dot in RELATED SAVES — the tile's hue with the tile taken away. */
export const SOURCE_DOT: Record<SaveSource, string> = {
  reddit: 'bg-orange-2',
  youtube: 'bg-danger-dot',
  article: 'bg-source-web',
  x: 'bg-muted',
}

/**
 * The SAME three hues again, as values rather than utilities — the one property the motion
 * system asks a component for (`--mo-rule`, index.css → THE MOTION SYSTEM). `.mo-ink` derives
 * the hover fill from it with `color-mix`, so a save card's hover says which system the save
 * came from before the pointer commits, and dark mode follows for free: every one of these
 * tokens is re-declared under `[data-theme='dark']` and the mix re-resolves.
 *
 * A second map rather than a parse of the first: `bg-orange-2` is a class name, and Tailwind
 * has already turned it into a rule by the time a component could read a value out of it.
 */
export const SOURCE_RULE: Record<SaveSource, string> = {
  reddit: 'var(--color-orange-2)',
  youtube: 'var(--color-danger-dot)',
  article: 'var(--color-source-web)',
  x: 'var(--color-muted)',
}

export function SourceTile({ source, size = 20 }: { source: SaveSource; size?: number }) {
  const Glyph =
    source === 'reddit' ? RedditFace : source === 'youtube' ? VideoBox : source === 'x' ? XMark : Document
  return (
    <span
      className={clsx(
        'flex flex-none items-center justify-center rounded-keycap',
        SOURCE_TILE[source],
      )}
      style={{ width: size, height: size }}
    >
      <Glyph size={Math.round(size * 0.6)} />
    </span>
  )
}

// ── The band's filter pills ─────────────────────────────────────────────────

/**
 * `Type` · `Source` · `Unread` · `Date`, and the dark `Inbox 12`.
 *
 * MOST OF THEM FILTER NOTHING YET, and each says so on hover rather than
 * silently doing nothing when pressed — the Connections screen's rule, applied
 * here: a control that renders as designed, keeps its keyboard focus, carries
 * `aria-disabled` and names the version that makes it work. The COUNT on the
 * Inbox pill is real and measured; it is the lens behind it that is not built.
 *
 * `onClick` IS WHAT MAKES A PILL LIVE, the same rule Connections' `ActionButton`
 * uses: passing it flips the pill from disabled-by-design to real — no
 * `aria-disabled`, a real `aria-pressed`, and `waitingFor` no longer applies
 * because there is nothing left to wait for. `Unread` is the one pill wired
 * this way (`SaveFilter.unread`, live in both `remote.ts` and `demo.ts`);
 * Type/Source/Date/Inbox still render through the disabled branch.
 */
export function FilterPill({
  label,
  count,
  dark,
  waitingFor,
  pressed,
  onClick,
}: {
  label: string
  count?: number
  dark?: boolean
  /** Ignored once `onClick` is passed — a live pill has nothing left to wait for. */
  waitingFor?: string
  /** Whether a LIVE pill's filter is currently applied. Unused on a disabled pill. */
  pressed?: boolean
  /** Present only on the pill actually wired to a real filter. */
  onClick?: () => void
}) {
  const live = onClick !== undefined
  const filled = dark || (live && pressed)
  return (
    <button
      type="button"
      aria-disabled={live ? undefined : 'true'}
      aria-pressed={live ? pressed : undefined}
      title={live ? undefined : waitingFor}
      onClick={live ? onClick : (e) => e.preventDefault()}
      // `--mo-rule` only matters for the live pill's `.mo-ink` fill; a disabled pill never reads
      // it (§2: no hover on an inert control).
      style={{ '--mo-rule': 'var(--color-interests)' } as CSSProperties}
      className={clsx(
        'flex flex-none items-center gap-[7px] whitespace-nowrap rounded-pill px-[11px] py-[6px] leading-none',
        live
          ? 'mo-ink mo-press'
          : ['cursor-not-allowed transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]', !dark && 'hover:bg-surface-2'],
        filled
          ? 'bg-ink text-[11px] font-semibold text-surface'
          : 'border border-line bg-surface text-[11px] font-medium text-ink-3',
      )}
    >
      {label}
      {count !== undefined && (
        <span className="rounded-pill bg-count-badge px-[6px] text-[10px] font-bold leading-[1.6] text-on-dark">
          {count}
        </span>
      )}
    </button>
  )
}

// ── Honest absence ──────────────────────────────────────────────────────────

/**
 * What a panel shows when its read threw. The library's reads all throw in
 * remote mode until V5, and an empty grid would read as "you have saved
 * nothing" — a claim, and a false one. The sentence the error carries names the
 * version instead.
 */
export function Unavailable({ error, className }: { error: unknown; className?: string }) {
  const message =
    error instanceof Error
      ? error.message
      : 'This could not be read, and the reason did not come back with a sentence attached.'
  return (
    <div
      className={clsx(
        'flex items-start gap-[9px] rounded-card border border-line bg-surface px-[17px] py-[15px]',
        className,
      )}
    >
      <span className="mt-[2px] size-[5px] flex-none rounded-full bg-microcopy" aria-hidden />
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">{message}</p>
    </div>
  )
}

/** A skeleton with the geometry of the thing it stands in for, never a spinner. */
export function Skeleton({ height, className }: { height: number; className?: string }) {
  return (
    <div
      className={clsx('animate-pulse rounded-card border border-line bg-surface', className)}
      style={{ height }}
      aria-hidden
    />
  )
}

/** A quiet, true sentence where a section has nothing in it. Never a blank space. */
export function QuietNote({ children }: { children: ReactNode }) {
  return <p className="text-[11px] leading-[1.45] text-microcopy text-pretty">{children}</p>
}
